/**
 * Workload-Steckbrief (NC-1279).
 *
 * Eigene Seite statt eines weiteren Blocks in WorkloadEditorPage — die hat
 * bereits 856 Zeilen. Leeres Profil => Wizard, vorhandenes => Leseansicht.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress,
  Step, StepLabel, Stepper, Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { workloadProfileApi, emptyProfileInput } from '../services/workloadProfileService';
import {
  ProfileAutoFacts,
  ProfileGapResult,
  WorkloadProfile,
  WorkloadProfileInput,
  WorkloadRetrospectives,
} from '../services/types';
import ProfileStepPurpose from '../components/workload/profile/ProfileStepPurpose';
import ProfileStepOwnership from '../components/workload/profile/ProfileStepOwnership';
import ProfileStepUsage from '../components/workload/profile/ProfileStepUsage';
import ProfileStepRequirements from '../components/workload/profile/ProfileStepRequirements';
import ProfileStepDeferControl from '../components/workload/profile/ProfileStepDeferControl';
import ProfileReadView from '../components/workload/profile/ProfileReadView';
import ProfileFactsAndGaps from '../components/workload/profile/ProfileFactsAndGaps';
import ProfileRetrospectives from '../components/workload/profile/ProfileRetrospectives';
import { useAuth } from '../contexts/AuthContext';

const STEP_LABELS = ['Zweck', 'Verantwortung', 'Nutzung', 'Voraussetzungen'];

export default function WorkloadProfilePage() {
  const { workloadId = '' } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canEdit = hasRole('operator');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<WorkloadProfile | null>(null);
  // NC-1280: measured usage + gap findings, delivered by the same GET as the
  // profile. Kept in their own state so a pre-NC-1280 response (both absent)
  // simply renders nothing extra.
  const [facts, setFacts] = useState<ProfileAutoFacts | null>(null);
  const [gaps, setGaps] = useState<ProfileGapResult | null>(null);
  // NC-1281: both feedback stages, delivered by the same GET. Same rule as
  // facts/gaps — absent on a pre-NC-1281 response, which renders nothing extra.
  const [retrospectives, setRetrospectives] = useState<WorkloadRetrospectives | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<WorkloadProfileInput>(emptyProfileInput());
  const [activeStep, setActiveStep] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await workloadProfileApi.get(workloadId);
      setProfile(result.profile);
      setFacts(result.facts ?? null);
      setGaps(result.gaps ?? null);
      setRetrospectives(result.retrospectives ?? null);
      if (result.profile) {
        setDraft(result.profile);
        setEditing(false);
      } else {
        setDraft(emptyProfileInput());
        setEditing(true);
      }
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    } finally {
      setLoading(false);
    }
  }, [workloadId]);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await workloadProfileApi.save(workloadId, draft);
      setProfile(saved);
      setDraft(saved);
      setEditing(false);
      setActiveStep(0);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button onClick={() => void load()}>Erneut laden</Button>}>
          Steckbrief konnte nicht geladen werden: {loadError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Steckbrief — {workloadId}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" onClick={() => navigate('/workloads')}>Zurück</Button>
      </Box>

      {!editing && profile && (
        <Card><CardContent>
          <ProfileReadView profile={profile} canEdit={canEdit} onEdit={() => setEditing(true)} />
          {/*
            NC-1280: below the profile, never in front of it — the findings inform,
            they do not gate the edit flow.
          */}
          <ProfileFactsAndGaps facts={facts} gaps={gaps} />
          {/*
            NC-1281 S4: the feedback loop. The Fazit sits above the individual
            run retrospectives inside this component — the step-back statement is
            what the reader came for, the per-run observations are its evidence.
          */}
          <ProfileRetrospectives
            workloadId={workloadId}
            retrospectives={retrospectives}
            canContribute={canEdit}
            onChanged={() => void load()}
          />
        </CardContent></Card>
      )}

      {editing && (
        <>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {STEP_LABELS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

          <Card><CardContent>
            {activeStep === 0 && <ProfileStepPurpose value={draft} onChange={setDraft} />}
            {activeStep === 1 && <ProfileStepOwnership value={draft} onChange={setDraft} />}
            {activeStep === 2 && <ProfileStepUsage value={draft} onChange={setDraft} />}
            {activeStep === 3 && <ProfileStepRequirements value={draft} onChange={setDraft} />}

            {/* NC-1382: writes this step's required paths to openItems (Spec section 9). */}
            <ProfileStepDeferControl step={activeStep} value={draft} onChange={setDraft} />

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>
                Zurück
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              {activeStep < STEP_LABELS.length - 1 && (
                <Button variant="contained" onClick={() => setActiveStep((s) => s + 1)}>
                  Weiter
                </Button>
              )}
              <Button variant="contained" color="primary" disabled={saving || !canEdit} onClick={() => void save()}>
                {saving ? 'Speichert…' : 'Speichern'}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Du kannst jederzeit speichern — unvollständige Steckbriefe werden als Entwurf geführt.
            </Typography>
          </CardContent></Card>
        </>
      )}
    </Box>
  );
}
